import Icon from "@mui/material/Icon";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";

import { useEffect, useState, useRef } from "react";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import MenuOpenRounded from "@mui/icons-material/MenuOpenRounded";
import MenuRounded from "@mui/icons-material/MenuRounded";
import { Box, Typography, UserProfileDropdown } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Constants from "constants/index";
import Screens from "constants/screens";
import Strings from "constants/strings";
import Resources from "commons/resources";

import { RootState } from "store";
import { useAuth } from "providers/authProvider";
import { showLoading } from "store/slice/common.slice";
import { ICodename, IRecordMenuDetail } from "commons/interfaces";
import { setMiniSidenav, useMaterialUIController } from "components/context";
import { drawerStyles, navbar, navbarContainer, navLink } from "components/Navbar/styles";



interface Props {
    light?: boolean;
    isMobile?: boolean;
    mainMenu?: IRecordMenuDetail[];
};

function Navbar({ light, isMobile, mainMenu }: Props): JSX.Element {
    const auth = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatchRedux = useDispatch();
    const anchorEl = useRef<HTMLButtonElement>(null);

    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav, transparentNavbar, darkMode } = controller;

    const w768 = useMediaQuery("(max-width: 768px)");

    const [open, setOpen] = useState(false);
    const [openDropDown, setOpenDropDown] = useState<any>(false);

    const userInfo = useSelector((state: RootState) => state.user?.userInfo);
    const organizationLogoUrl = useSelector((state: RootState) => state.user.organizationLogoUrl);
    const { menuDetails } = useSelector((state: RootState) => state.titleRoute);

    useEffect(() => {
        const handleSetOpenMenu = () => setOpenDropDown(anchorEl.current);
        __EventEmitter.addListener(Constants.EventName.CHOOSE_ORGANIZATION, handleSetOpenMenu);
        return () => {
            __EventEmitter.removeListener(Constants.EventName.CHOOSE_ORGANIZATION, handleSetOpenMenu);
        };
    }, []);

    useEffect(() => {
        const options: ICodename[] = [];
        for (const element of [...userInfo?.currentServiceOrganizations || []]) {
            const nameOrg = Helpers.getDefaultValueMultiLanguage(element.name, "vi", true);
            options.push({
                name: nameOrg,
                code: element.id,
            });
        };

        const workingOrganization = Helpers.getItemInLocalStorage(Constants.StorageKeys.ORGANIZATION_ID) as string;

        if (options.length === 0) {
            return;
        };

        if (!workingOrganization) {
            Helpers.setItemInLocalStorage(Constants.StorageKeys.ORGANIZATION_ID, options?.[0]?.code || "");
            auth.signinRedirect({
                "active_user": userInfo?.userProfile?.identityId,
                "organization_id": options?.[0]?.code,
            })
        };
    }, [
        userInfo?.userProfile?.identityId,
        userInfo?.currentServiceOrganizations,
    ]);

    const handleClose = (event: any) => {
        if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
            return;
        }
        setOpen(false);
    };

    const handleLogout = () => {
        Helpers.showConfirmAlert(
            Strings.MESSAGE.CONFIRM_LOGOUT,
            async () => { await auth.signoutRedirect(); },
        );
    };

    const handleChangeOrganization = async (value: string) => {
        dispatchRedux(showLoading(true));

        Helpers.setItemInLocalStorage(Constants.StorageKeys.FROM, "");
        Helpers.setItemInLocalStorage(Constants.StorageKeys.ORGANIZATION_ID, value);

        await auth.signinRedirect({
            "organization_id": value,
            "active_user": userInfo?.userProfile?.identityId,
        });

        dispatchRedux(showLoading(false));
    };

    const handleNavigate = (item: IRecordMenuDetail) => {
        let redirectUrl = item.externalUrl;

        if (item.resourceURI === Constants.MenuResourceURI.SETTING) {
            const arrSub = [...menuDetails || []].filter(el => (
                !Helpers.isNullOrEmpty(el.externalUrl)
                && el.gene.startsWith(item.gene)
            ));

            redirectUrl = arrSub[0]?.externalUrl;
        } else {
            redirectUrl = item.externalUrl;
        }

        redirectUrl && navigate(redirectUrl);
    };

    const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }: { palette: any; functions: any }) => ({
        color: () => {
            let colorValue = light || darkMode ? white.main : dark.main;
            if (transparentNavbar && !light) {
                colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
            }
            return colorValue;
        },
    });

    const renderItemMenu = (isMobile: boolean) => {
        const firstItemIndex = 0;
        const lastItemIndex = mainMenu.length - 1;

        return [...mainMenu || []].map((item, index) => {
            let gene = getGeneByItem(location?.pathname, item);

            if (Helpers.isNullOrEmpty(gene)) {
                gene = getGeneByDataMenu(location?.pathname, menuDetails);
            };

            const isActive = `${gene}`.startsWith(`${item.gene}`);
            const title = Helpers.getDefaultValueMultiLanguage(item.title, Constants.DefaultLanguage, true);
            return (
                <Box
                    key={index}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleNavigate(item);
                        setOpen(false);
                    }}
                    sx={theme => navLink(theme, { isActive, isMobile })}
                    style={!isMobile ? {
                        marginLeft: index === firstItemIndex && "auto",
                        marginRight: index === lastItemIndex && "auto"
                    } : null}
                >
                    <Typography variant="button" className="prevent-select">
                        {title}
                    </Typography>
                </Box>
            );
        });
    };

    return (
        <AppBar
            color={"inherit"}
            position={"static"}
            sx={(theme) => navbar(theme, { transparentNavbar, light, darkMode })}
        >
            <Toolbar sx={(theme) => navbarContainer(theme)}>
                <Box
                    sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    onClick={() => {
                        const url = Helpers.getItemInLocalStorage(Constants.StorageKeys.FIRST_URL) || Screens.HOME;
                        navigate(url);
                    }}>
                    <img
                        width={55}
                        height={55}
                        alt={"logo_menu"}
                        src={organizationLogoUrl || Resources.Images.LOGO_APP}
                    />
                </Box>

                {!isMobile && ([...mainMenu || []].length > 0) &&
                    <Box sx={{
                        display: "flex",
                        gap: "10px",
                        mx: "6px",
                        pb: "2px",
                        height: "40px",
                        width: "64vw",
                        overflowX: "auto",
                    }}>
                        {renderItemMenu(false)}
                    </Box>
                }

                <Box color={light ? Constants.Styles.WHITE_COLOR : "inherit"}>
                    <UserProfileDropdown
                        anchorEl={anchorEl}
                        openDropDown={openDropDown}
                        setOpenDropDown={setOpenDropDown}
                        pathNameProfile={Screens.PROFILE}
                        hiddenItemDropDown={{
                            itemManagementOrganization: true,
                        }}
                        uncheckedOrganization
                        onLogout={handleLogout}
                        onChangeOrganization={handleChangeOrganization}
                        onNavigate={(pathName) => { navigate(pathName); }}
                    />
                    {isMobile &&
                        <IconButton
                            disableRipple
                            size={"small"}
                            color={"inherit"}
                            onClick={() => {
                                setMiniSidenav(dispatch, !miniSidenav);
                                setOpen(prev => !prev);
                            }}
                        >
                            <Icon sx={iconsStyle} fontSize="medium">
                                {open ? (
                                    <MenuRounded sx={{ color: Constants.Styles.BLACK_COLOR }} />
                                ) : (
                                    <MenuOpenRounded sx={{ color: Constants.Styles.BLACK_COLOR }} />
                                )}
                            </Icon>
                        </IconButton>
                    }
                    <Drawer anchor={"right"} open={open} onClose={handleClose}>
                        <Box
                            role="presentation"
                            onClick={handleClose}
                            onKeyDown={handleClose}
                            sx={drawerStyles}
                        >
                            <Box sx={{
                                gap: 1,
                                display: "flex",
                                flexDirection: "column",
                            }}>
                                {renderItemMenu(true)}
                            </Box>
                        </Box>
                    </Drawer>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Navbar;

const getGeneByItem = (pathname: string, item: IRecordMenuDetail) => {
    let value = "";
    if (pathname === item.externalUrl) {
        value = item.gene;
    } else {
        if (!Helpers.isNullOrEmpty(item.extraInformation)) {
            const extraInformation = JSON.parse(item.extraInformation);
            if (Array.isArray(extraInformation)) {
                const itemExtra = [...extraInformation || []].find(i => (
                    pathname === i.ScreenPath ||
                    pathname === i.screenPath
                ));
                !Helpers.isNullOrEmpty(itemExtra) && (value = itemExtra?.Gene || itemExtra?.gene);
            } else {
                const path = extraInformation?.ScreenPath || extraInformation?.screenPath;
                if (path === pathname) {
                    value = extraInformation?.Gene || extraInformation?.gene;
                };
            }
        }
    };
    return value;
};

const getGeneByDataMenu = (pathname: string, data: IRecordMenuDetail[]) => {
    let value = "";
    for (const element of [...data || []]) {
        if (pathname === element.externalUrl) {
            value = element?.gene;
        } else {
            const extraInformation = Helpers.converStringToJson(element.extraInformation);
            if (Array.isArray(extraInformation)) {
                const itemExtra = [...extraInformation || []].find(i => (
                    pathname === i.ScreenPath ||
                    pathname === i.screenPath
                ));
                !Helpers.isNullOrEmpty(itemExtra) && (value = itemExtra?.Gene || itemExtra?.gene);
            } else {
                const path = extraInformation?.ScreenPath || extraInformation?.screenPath;
                if (path === pathname) {
                    value = extraInformation?.Gene || extraInformation?.gene;
                };
            }
        };
    };

    return value;
};